import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchLivePaymentOperations, updateLivePaymentOperation } from '../api/admin';
import { getAdminLocale, localizeApiError, localizeServerMessage, t } from '../i18n';
import './LivePaymentOperations.css';

const STATUS_LABEL = { IN_PROGRESS: '진행 중', DONE: '완료 확인 중', HOLD: '보류/검토', CRITICAL_REVIEW: '검토 필요' };
const TYPE_LABEL = { deposit: '입금', withdraw: '출금', withdraw_refund: '출금 환불' };

function money(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n.toLocaleString(getAdminLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
}
function date(v) {
  if (!v) return '-';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(getAdminLocale());
}
function clip(v = '') { const s = String(v || '').trim(); return s || '-'; }

export default function LivePaymentOperations() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState('');
  const [rowMessage, setRowMessage] = useState({});
  const mounted = useRef(true);
  const requestSerial = useRef(0);
  const pendingLoads = useRef(0);

  const load = useCallback(async (silent = false) => {
    const serial = ++requestSerial.current;
    pendingLoads.current += 1;
    const current = () => mounted.current && serial === requestSerial.current;
    try {
      if (!silent) setLoading(true);
      const r = await fetchLivePaymentOperations({ page, pageSize, type: type || undefined, status: status || undefined });
      if (!current()) return;
      // A failed cashier query must never be interpreted as 0 transactions.
      if (r?.ok === false || !Array.isArray(r?.items)) throw new Error(r?.message || '실시간 결제 목록 응답이 올바르지 않습니다.');
      setItems(r.items);
      setTotal(Math.max(0, Number(r?.total || 0)));
      setError('');
    } catch (e) {
      if (current()) setError(localizeApiError(e, '목록 조회 실패'));
    } finally {
      pendingLoads.current = Math.max(0, pendingLoads.current - 1);
      if (current() && !silent) setLoading(false);
    }
  }, [type, status, page, pageSize]);

  useEffect(() => {
    mounted.current = true;
    load(false);
    // Never allow an older, slower response to replace a newer snapshot.
    const id = setInterval(() => { if (pendingLoads.current === 0) load(true); }, 3000);
    return () => { mounted.current = false; requestSerial.current += 1; clearInterval(id); };
  }, [load]);

  const counts = useMemo(() => ({
    total: items.length,
    review: items.filter((x) => x?.problem_hold === true || ['HOLD', 'CRITICAL_REVIEW'].includes(String(x.status).toUpperCase())).length,
    critical: items.filter((x) => String(x.status).toUpperCase() === 'CRITICAL_REVIEW').length,
  }), [items]);

  async function act(row, action) {
    const key = `${row.request_id}:${action}`;
    if (busy) return;
    const st = String(row.status || '').toUpperCase();
    const payload = { action };

    if (action === 'approve' && !window.confirm(t('{user} / {amount} {asset} 거래를 승인(거래완료) 처리할까요?\n실제 자산/원장 처리까지 수행됩니다.', {
      user: row.user_code,
      amount: money(row.amount),
      asset: row.asset_code || 'USDT',
    }))) return;
    if (action === 'reject' && !window.confirm(t('{user} 거래를 거절 처리할까요?\n출금의 경우 실행 이후 상태면 안전장치에 의해 거절이 차단될 수 있습니다.', { user: row.user_code }))) return;
    if (action === 'hold' && !window.confirm(t('이 거래를 보류 상태로 잠글까요?\n이미 시작된 온체인 송금 자체를 중단하는 기능은 아닙니다.'))) return;
    if (action === 'cancel' && !window.confirm(t('{user} 거래에 관리자 취소를 요청할까요?\n브로드캐스트 전이면 즉시 취소/예약금 복구하고, 이미 체인 전송 가능성이 있으면 자동 환불하지 않고 검토 상태를 유지합니다.', { user: row.user_code }))) return;

    if (st === 'CRITICAL_REVIEW' && action !== 'hold') {
      if (!window.confirm(t('CRITICAL_REVIEW 거래입니다. DB 자산/원장/온체인 상태를 대조 확인한 뒤 처리하는 것이 맞습니까?'))) return;
      payload.confirm_reconciliation = true;
      if (action === 'reject' && String(row.request_type).toLowerCase() === 'withdraw') {
        if (!window.confirm(t('이 출금은 온체인 전송 및 브로드캐스트가 없음을 확인했습니까? 확인되지 않았다면 취소하세요.'))) return;
        payload.confirm_no_onchain_transfer = true;
      }
    }

    try {
      setBusy(key);
      setError('');
      setRowMessage((prev) => ({ ...prev, [row.request_id]: t('처리 중...') }));
      const result = await updateLivePaymentOperation(row.request_id, payload);
      setRowMessage((prev) => ({ ...prev, [row.request_id]: result?.message || result?.resolved?.message ||
        (result?.cancel_pending ? t('취소 요청 접수: 확정 대기 중입니다.') : t('처리가 완료되었습니다.')) }));
      await load(true);
    } catch (e) {
      const message = localizeApiError(e, '처리 실패');
      setError(message);
      setRowMessage((prev) => ({ ...prev, [row.request_id]: message }));
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="livepay-page">
      <div className="livepay-head">
        <div>
          <h1>{t('실시간 결제상황')}</h1>
          <p>{t('진행 중 거래부터 보류·검토 필요 거래까지 3초 간격으로 자동 갱신합니다.')}</p>
        </div>
        <button onClick={() => load(false)} disabled={loading}>{t('새로고침')}</button>
      </div>

      <div className="livepay-summary">
        <div><b>{counts.total}</b><span>{t('현재 표시')}</span></div>
        <div><b>{counts.review}</b><span>{t('보류/검토')}</span></div>
        <div className="critical"><b>{counts.critical}</b><span>CRITICAL</span></div>
      </div>

      <div className="livepay-filter">
        <select value={type} onChange={(e) => { setPage(1); setType(e.target.value); }}>
          <option value="">{t('입출금 전체')}</option>
          <option value="deposit">{t('입금')}</option>
          <option value="withdraw">{t('출금')}</option>
          <option value="withdraw_refund">{t('출금 환불')}</option>
        </select>
        <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }}>
          <option value="">{t('열린 상태 전체')}</option>
          <option value="IN_PROGRESS">{t('진행 중')}</option>
          <option value="DONE">{t('완료 확인 중')}</option>
          <option value="HOLD">{t('보류')}</option>
          <option value="CRITICAL_REVIEW">{t('검토 필요')}</option>
        </select>
      </div>

      <div className="livepay-pagination" aria-label={t('결제 목록 페이지')}>
        <span>{t('총')} {total} / {t('페이지')} {page} · {pageSize}{t('건씩')}</span>
        <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((v) => Math.max(1, v - 1))}>{t('이전')}</button>
        <button type="button" disabled={page * pageSize >= total || loading} onClick={() => setPage((v) => v + 1)}>{t('다음')}</button>
      </div>

      {error && <div className="livepay-error">{error}</div>}

      <div className="livepay-table-wrap">
        <table className="livepay-table">
          <thead>
            <tr>
              <th>{t('구분 / 상태')}</th>
              <th>{t('유저(유저코드) / 닉네임')}</th>
              <th>{t('신청 금액')}</th>
              <th>{t('등록 지갑 주소')}</th>
              <th>{t('배정 지갑 / 브릿지')}</th>
              <th>{t('거래시간')}</th>
              <th>{t('TX / 단계')}</th>
              <th>{t('관리')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="empty">{t('불러오는 중...')}</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="8" className="empty">{t('현재 진행/검토 중인 결제가 없습니다.')}</td></tr>
            ) : items.map((row) => {
              const st = String(row.status || '').toUpperCase();
              const problemHold = row?.problem_hold === true || String(row.result || '').toUpperCase() === 'WITHDRAW_PROBLEM_HOLD';
              const statusLabel = problemHold ? t('문제-보류') : (STATUS_LABEL[st] ? t(STATUS_LABEL[st]) : st);
              const waitingWallet = row?.wallet_queue_waiting === true;
              const isReview = st === 'HOLD' || st === 'CRITICAL_REVIEW';
              const canHold = !waitingWallet && ['IN_PROGRESS', 'DONE'].includes(st);
              const canApprove = !waitingWallet && isReview;
              const canReject = !waitingWallet && ['IN_PROGRESS', 'HOLD', 'CRITICAL_REVIEW'].includes(st);
              const canCancel = waitingWallet || ['IN_PROGRESS', 'HOLD', 'CRITICAL_REVIEW'].includes(st);
              return (
                <tr key={row.request_id} className={`livepay-record ${st === 'CRITICAL_REVIEW' ? 'is-critical' : ''}`}>
                  <td data-label={t('구분 / 상태')}>
                    <span className={`type ${row.request_type}`}>{TYPE_LABEL[row.request_type] ? t(TYPE_LABEL[row.request_type]) : row.request_type}</span>
                    <span className={`status ${problemHold ? 's-PROBLEM_HOLD' : `s-${st}`}`}>{statusLabel}</span>
                  </td>
                  <td data-label={t('유저(유저코드) / 닉네임')}><b>{clip(row.user_code)}</b><small>{clip(row.nickname)}</small></td>
                  <td data-label={t('신청 금액')}>
                    <b>{String(row.request_type).toLowerCase() === 'deposit'
                      ? `${money(row.received_amount)} / ${money(row.target_amount || row.amount)} ${row.asset_code || 'USDT'}`
                      : `${money(row.amount)} ${row.asset_code || 'USDT'}`}</b>
                    <small>{row.network_code || '-'} / {row.network_name || '-'}</small>
                  </td>
                  <td data-label={t('등록 지갑 주소')} className="mono">{clip(row.registered_wallet_address)}</td>
                  <td data-label={t('배정 지갑 / 브릿지')} className="mono"><b>{clip(row.assigned_wallet_or_bridge)}</b><small>bridge: {clip(row.bridge_id)}</small></td>
                  <td data-label={t('거래시간')}>{date(row.created_at)}<small>{t('갱신')} {date(row.updated_at)}</small></td>
                  <td data-label={t('TX / 단계')} className="mono">{clip(row.tx_hash || row.tx_id)}<small>{clip(row.step || row.result)}</small>{row.message ? <small title={localizeServerMessage(row.message, '결제 처리 상태 메시지를 확인할 수 없습니다.')}>{localizeServerMessage(row.message, '결제 처리 상태 메시지를 확인할 수 없습니다.')}</small> : null}</td>
                  <td data-label={t('관리')} className="livepay-actions-cell">
                    <div className="actions">
                      <button className="cancel" disabled={!!busy || !canCancel} title={!canCancel ? t('현재 상태에서는 관리자 취소할 수 없습니다.') : undefined} onClick={() => act(row, 'cancel')}>{busy === `${row.request_id}:cancel` ? t('처리중') : t('취소')}</button>
                      <button className="hold" disabled={!!busy || !canHold} title={!canHold ? t('이미 보류 중이거나 지갑 대기 상태입니다.') : undefined} onClick={() => act(row, 'hold')}>{busy === `${row.request_id}:hold` ? t('처리중') : t('보류')}</button>
                      <button className="approve" disabled={!!busy || !canApprove} title={!canApprove ? t('승인은 보류/검토 거래에서만 가능합니다.') : undefined} onClick={() => act(row, 'approve')}>{busy === `${row.request_id}:approve` ? t('처리중') : t('승인')}</button>
                      <button className="reject" disabled={!!busy || !canReject} title={!canReject ? t('지갑 배정 대기 거래는 취소만 가능합니다.') : undefined} onClick={() => act(row, 'reject')}>{busy === `${row.request_id}:reject` ? t('처리중') : t('거절')}</button>
                    </div>
                    {rowMessage[row.request_id] ? <small className="action-result">{rowMessage[row.request_id]}</small> : null}
                    <small className="reqid">{row.request_id}</small>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
